import React, {FC, useEffect} from 'react';

import AemButton from '@/components/Aem/AemButton';
import useConfigExternalLink from '@/hooks/externalLink/useConfigExternalLink';
import {useNewBadgeInfo} from '@/hooks/loyalty/dataHooks';
import appContent from '@/services/content/appContent';
import {getAchievedBadgeImage} from '@/services/loyalty/badgeUtils';
import {twx} from '@/utils/tailwindUtils';
import {LoyaltyPopupTemplate} from '@/views/Loyalty/PopupTemplates/primitives';

interface INewBadgePopupTemplateProps {
    onClose?: () => void;
    onGotIt?: () => void;
    isChipVisible?: boolean;
    isDescriptionVisible?: boolean;
    journeyId?: string;
    className?: string;
    headerClassName?: string;
}

export const NewBadgePopupTemplate: FC<INewBadgePopupTemplateProps> = ({
    onClose,
    onGotIt,
    isChipVisible,
    isDescriptionVisible,
    journeyId,
    className,
    headerClassName,
}) => {
    const {progressTitle, label, button} = appContent.getNewBadgeContent();
    const {externalLink, alt} = button || {};

    const {actionLabel, actionDescription, progressCount, totalCount, journeyName, journeyDescription, badge} =
        useNewBadgeInfo(journeyId) || {};

    const {redirectToExternalLink: redirect} = useConfigExternalLink({...externalLink, ssoParams: {journeyId}});

    useEffect(() => {
        if (!badge) {
            onClose?.();
        }
    }, []);

    const handleGotItButtonClick = () => {
        onGotIt?.();
        redirect?.();
    };

    if (!badge) {
        return null;
    }

    return (
        <LoyaltyPopupTemplate className={className}>
            <LoyaltyPopupTemplate.Header handleClose={onClose} className={twx('justify-between', headerClassName)}>
                {isChipVisible && <LoyaltyPopupTemplate.Chip>{label}</LoyaltyPopupTemplate.Chip>}
            </LoyaltyPopupTemplate.Header>
            <LoyaltyPopupTemplate.Body>
                <LoyaltyPopupTemplate.Hero className='text-center' imageSrc={getAchievedBadgeImage(badge)?.imageURL}>
                    <LoyaltyPopupTemplate.Title className='mt-2.5'>{journeyName}</LoyaltyPopupTemplate.Title>
                    {isDescriptionVisible && (
                        <LoyaltyPopupTemplate.Description>{journeyDescription}</LoyaltyPopupTemplate.Description>
                    )}
                </LoyaltyPopupTemplate.Hero>
                <LoyaltyPopupTemplate.Footer className='bg-charcoal'>
                    <div className='flex w-full flex-col gap-4'>
                        <div className='flex w-full justify-between'>
                            <div className='pr-5 text-16 font-normal'>
                                <p className='text-tertiary'>{actionLabel}</p>
                                <p className='text-white'>{actionDescription}</p>
                            </div>
                        </div>
                        <LoyaltyPopupTemplate.Progress
                            title={progressTitle}
                            currentStep={progressCount!}
                            totalSteps={totalCount!}
                        />
                    </div>
                </LoyaltyPopupTemplate.Footer>
                <AemButton
                    aria-label={alt}
                    primary
                    dark
                    className='m-0 w-full px-6 py-3'
                    onClick={handleGotItButtonClick}
                    {...button}
                />
            </LoyaltyPopupTemplate.Body>
        </LoyaltyPopupTemplate>
    );
};
